import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import { debounce } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';

import type { SwellsTimestep } from 'types/swell';
import useSetupForecastGraphEnergy from '../../hooks/useSetupForecastGraphEnergy';

import styles from './ForecastGraphEnergyContainer.module.scss';

const cx = classNames.bind(styles);

export interface ForecastGraphEnergyContainerProps {
  data: Array<SwellsTimestep>;
  numberOfTicks: number;
  overallMaxEnergy: number;
}

const ForecastGraphEnergyContainer: React.FC<ForecastGraphEnergyContainerProps> = ({
  data,
  numberOfTicks,
  overallMaxEnergy,
}) => {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { height: graphHeight = 100, width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const { setupForecastGraphEnergy } = useSetupForecastGraphEnergy({
    data,
    graphHeight,
    graphWidth,
    numberOfTicks,
    overallMaxEnergy,
  });

  const debouncedSetupForecastGraphEnergy = useMemo(
    () => debounce(setupForecastGraphEnergy, 100, { leading: true, trailing: true }),
    [setupForecastGraphEnergy],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetupForecastGraphEnergy();

    return () => {
      debouncedSetupForecastGraphEnergy.cancel();
    };
  }, [graphWidth, debouncedSetupForecastGraphEnergy]);

  return (
    <div className={cx(['swell-energy-graph', styles.wrapper])}>
      <div className={cx(['swell-energy-graph-canvas', styles.canvas])} ref={canvasRef} />
    </div>
  );
};

export default ForecastGraphEnergyContainer;
